import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { JarvisThemeProvider } from './theme/JarvisThemeProvider';
import reportWebVitals from './reportWebVitals';

// Suppress benign ResizeObserver errors BEFORE React mounts
(() => {
  const swallowRO = (e: any) => {
    const msg = e?.message || e?.reason?.message || '';
    if (typeof msg === 'string' && (
      msg.includes('ResizeObserver loop limit exceeded') ||
      msg.includes('ResizeObserver loop completed with undelivered notifications')
    )) {
      e.preventDefault?.();
      e.stopImmediatePropagation?.();
      return true;
    }
    return false;
  };
  window.addEventListener('error', (event) => {
    if (swallowRO(event)) return;
    // eslint-disable-next-line no-console
    console.error('Global error caught:', event.error);
  }, { capture: true });
  window.addEventListener('unhandledrejection', (event) => {
    if (swallowRO(event)) return;
    // eslint-disable-next-line no-console
    console.error('Unhandled promise rejection:', event.reason);
  }, { capture: true });

  // Dev-only: suppress React overlay by filtering console.error for RO warnings
  if (process.env.NODE_ENV === 'development') {
    const originalConsoleError = console.error;
    const shouldSuppress = (...args: any[]): boolean => {
      const patterns = [
        /ResizeObserver loop limit exceeded/i,
        /ResizeObserver loop completed with undelivered notifications/i
      ];
      return args.some((arg) => {
        if (!arg) return false;
        if (typeof arg === 'string') {
          return patterns.some((p) => p.test(arg));
        }
        if (arg && typeof arg.message === 'string') {
          return patterns.some((p) => p.test(arg.message));
        }
        try {
          const s = String(arg);
          return patterns.some((p) => p.test(s));
        } catch (_) {
          return false;
        }
      });
    };
    console.error = (...args: any[]) => {
      if (shouldSuppress(...args)) return;
      originalConsoleError(...args);
    };

    // As a last resort in dev, hide the runtime overlay iframe when the message matches RO warnings
    const hideOverlayIfRO = () => {
      try {
        const iframes = Array.from(document.querySelectorAll('iframe')) as HTMLIFrameElement[];
        for (const frame of iframes) {
          const doc = frame.contentDocument || frame.contentWindow?.document;
          if (!doc) continue;
          const text = doc.body?.innerText || '';
          if (/ResizeObserver loop (limit exceeded|completed with undelivered notifications)/i.test(text)) {
            frame.style.display = 'none';
          }
        }
      } catch {}
    };
    const mo = new MutationObserver(hideOverlayIfRO);
    mo.observe(document.documentElement, { childList: true, subtree: true });
    // Also run periodically in case overlay mutates asynchronously
    const intervalId = window.setInterval(hideOverlayIfRO, 500);
    window.addEventListener('beforeunload', () => {
      try { mo.disconnect(); } catch {}
      try { window.clearInterval(intervalId); } catch {}
    });
  }
})();

try {
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Root element not found');
  }

  if (process.env.NODE_ENV === 'production') {
    // Silence non-error console in production to keep console clean
    // eslint-disable-next-line no-console
    console.log = () => {};
    // eslint-disable-next-line no-console
    console.info = () => {};
    // eslint-disable-next-line no-console
    console.debug = () => {};
    // Keep console.error to surface real issues
  }

  // eslint-disable-next-line no-console
  console.log('Initializing React application...');
  const root = ReactDOM.createRoot(rootElement);

  root.render(
    // <React.StrictMode>
      <JarvisThemeProvider>
        <App />
      </JarvisThemeProvider>
    // </React.StrictMode>
  );
  // eslint-disable-next-line no-console
  console.log('React application initialized successfully');
} catch (error) {
  // eslint-disable-next-line no-console
  console.error('Failed to initialize React application:', error);
  // Add a visible error message to the page
  document.body.innerHTML = `
    <div style="padding: 20px; font-family: system-ui, -apple-system, sans-serif;">
      <h1 style="color: #d32f2f;">Application Error</h1>
      <p>Failed to initialize the application. Please check the console for details.</p>
      <pre style="background: #f5f5f5; padding: 10px; border-radius: 4px;">${error}</pre>
    </div>
  `;
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();